import React, { useState, useEffect, useCallback } from 'react';

const API = (path, opts = {}) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const baseHeaders = { 'Content-Type': 'application/json', ...opts.headers };
  if (token) {
    baseHeaders.Authorization = `Bearer ${token}`;
  }
  return fetch(path, { ...opts, headers: baseHeaders });
};

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => (
  <span style={{
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    background: status === 'published' ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
    color: status === 'published' ? '#22c55e' : 'var(--xfd-muted)',
    border: `1px solid ${status === 'published' ? 'rgba(34,197,94,0.3)' : 'rgba(148,163,184,0.3)'}`,
  }}>
    {status === 'published' ? '● Published' : '○ Draft'}
  </span>
);

function SubmissionsPanel({ form, authToken, onClose }) {
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    setLoading(true);
    API(`/xavvy-forms/${form.id}/submissions?skip=${page * pageSize}&limit=${pageSize}`)
      .then(r => r.json()).then(d => { setSubs(d.items || []); setTotal(d.total || 0); }).finally(() => setLoading(false));
  }, [form.id, page]);

  const handleDelete = async (subId) => {
    if (!confirm('Delete this submission?')) return;
    await API(`/xavvy-forms/${form.id}/submissions/${subId}`, { method: 'DELETE' });
    setSubs(s => s.filter(x => x.id !== subId));
    setTotal(t => t - 1);
  };

  const defn = form.definition || {};
  const allFields = [];
  const walk = (elems) => { (elems || []).forEach(e => { if (e.name) allFields.push(e); if (e.elements) walk(e.elements); if (e.columns) e.columns.forEach(c => walk(c.elements)); if (e.tabs) e.tabs.forEach(t => walk(t.elements)); }); };
  walk(defn.elements || []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--xfd-panel)', border: '1px solid var(--xfd-border)', borderRadius: 12, width: '85vw', maxWidth: 900, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--xfd-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{form.name} — Submissions</div>
            <div style={{ fontSize: 12, color: 'var(--xfd-dim)', marginTop: 2 }}>{total} total submission{total !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--xfd-muted)', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 20px' }}>
          {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--xfd-dim)' }}>Loading…</div> : subs.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--xfd-dim)' }}>No submissions yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16, fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--xfd-dim)', borderBottom: '1px solid var(--xfd-border)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 500 }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 500 }}>IP</th>
                  {allFields.slice(0, 4).map(f => <th key={f.name} style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 500 }}>{f.label || f.name}</th>)}
                  <th style={{ padding: '8px 6px' }}></th>
                </tr>
              </thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--xfd-border)' }}>
                    <td style={{ padding: '8px 6px', color: 'var(--xfd-text)' }}>{fmt(s.submitted_at)}</td>
                    <td style={{ padding: '8px 6px', color: 'var(--xfd-dim)', fontFamily: 'monospace', fontSize: 11 }}>{s.ip_address || '—'}</td>
                    {allFields.slice(0, 4).map(f => {
                      const val = s.data?.[f.name];
                      return <td key={f.name} style={{ padding: '8px 6px', color: 'var(--xfd-text)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeof val === 'object' ? JSON.stringify(val) : String(val ?? '—')}</td>;
                    })}
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <button onClick={() => handleDelete(s.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {total > pageSize && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-text)', cursor: 'pointer', opacity: page === 0 ? 0.4 : 1 }}>‹ Prev</button>
              <span style={{ padding: '6px 14px', color: 'var(--xfd-dim)', fontSize: 12 }}>Page {page + 1} of {Math.ceil(total / pageSize)}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * pageSize >= total} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-text)', cursor: 'pointer', opacity: (page + 1) * pageSize >= total ? 0.4 : 1 }}>Next ›</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalyticsPanel({ form, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    API(`/xavvy-forms/${form.id}/analytics`)
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(d => setData(d))
      .catch(() => setError('Could not load analytics.'))
      .finally(() => setLoading(false));
  }, [form.id]);

  const fmtSec = (s) => {
    if (s == null) return '—';
    if (s < 60) return `${Math.round(s)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  };

  const funnelSteps = data ? [
    { label: 'Views', value: data.funnel?.views ?? 0, color: '#3b82f6' },
    { label: 'Started', value: data.funnel?.starts ?? 0, color: '#8b5cf6' },
    { label: 'Submitted', value: data.funnel?.submits ?? 0, color: '#22c55e' },
  ] : [];
  const maxFunnel = funnelSteps.length > 0 ? Math.max(...funnelSteps.map(s => s.value), 1) : 1;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--xfd-panel)', border: '1px solid var(--xfd-border)', borderRadius: 14, width: 'min(680px, 94vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--xfd-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--xfd-text)' }}>📊 {form.name} — Analytics</div>
            <div style={{ fontSize: 12, color: 'var(--xfd-dim)', marginTop: 2 }}>All data on-instance. No third-party tracking.</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--xfd-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--xfd-dim)' }}>Loading analytics…</div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#ef4444' }}>{error}</div>
          ) : (
            <>
              {/* KPI row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
                {[
                  { label: 'Views', value: data.funnel?.views ?? 0, color: '#3b82f6' },
                  { label: 'Submissions', value: data.funnel?.submits ?? 0, color: '#22c55e' },
                  { label: 'Abandons', value: data.funnel?.abandons ?? 0, color: '#f59e0b' },
                  { label: 'Conversion', value: `${data.conversion_rate ?? 0}%`, color: '#a78bfa' },
                  { label: 'Avg Time', value: fmtSec(data.avg_completion_seconds), color: '#38bdf8' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: 'var(--xfd-input)', border: '1px solid var(--xfd-border)', borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Funnel chart */}
              <div style={{ background: 'var(--xfd-input)', border: '1px solid var(--xfd-border)', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 14 }}>Submission Funnel</div>
                {funnelSteps.map(step => (
                  <div key={step.label} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--xfd-muted)', marginBottom: 4 }}>
                      <span>{step.label}</span>
                      <span style={{ fontWeight: 600, color: 'var(--xfd-text)' }}>{step.value}</span>
                    </div>
                    <div style={{ height: 8, background: 'var(--xfd-panel)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(step.value / maxFunnel) * 100}%`, background: step.color, borderRadius: 4, transition: 'width 0.4s ease' }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Field abandonment */}
              {data.field_abandonment && Object.keys(data.field_abandonment).length > 0 && (
                <div style={{ background: 'var(--xfd-input)', border: '1px solid var(--xfd-border)', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 12 }}>Field Abandonment (focus without blur)</div>
                  {Object.entries(data.field_abandonment).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([field, count]) => (
                    <div key={field} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--xfd-border)' }}>
                      <span style={{ color: 'var(--xfd-text)', fontFamily: 'monospace' }}>{field}</span>
                      <span style={{ color: '#f59e0b', fontWeight: 600 }}>{count}×</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Embed snippet */}
              <div style={{ background: 'var(--xfd-input)', border: '1px solid var(--xfd-border)', borderRadius: 10, padding: '16px 20px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 8 }}>Embed this form</div>
                <div style={{ fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 10 }}>Add to any website with a single script tag:</div>
                <pre style={{ background: 'var(--xfd-input)', border: '1px solid var(--xfd-border)', borderRadius: 6, padding: '10px 12px', fontSize: 11, color: '#a5b4fc', overflowX: 'auto', margin: 0 }}>{`<div data-xavvy-token="${form.token}" data-xavvy-mode="inline"></div>
<script src="${window.location.origin}/embed/xf.js"></script>`}</pre>
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--xfd-dim)' }}>
                  <strong>Modes:</strong> <code>inline</code> (default) · <code>modal</code> · <code>slide</code><br />
                  Listen for submissions: <code>el.addEventListener('xavvy:submit', e =&gt; console.log(e.detail))</code>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FormCard({ form, authToken, onDesign, onDelete, onToggleStatus, onDuplicate, onMove, onViewSubmissions, onViewAnalytics }) {
  const publicUrl = `${window.location.origin}/xf/${form.token}`;
  const [copied, setCopied] = useState(false);

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ background: 'var(--xfd-panel)', border: '1px solid var(--xfd-border)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--xfd-text)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.name}</div>
          {form.description && <div style={{ fontSize: 12, color: 'var(--xfd-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.description}</div>}
        </div>
        <StatusBadge status={form.status} />
      </div>

      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--xfd-dim)' }}>
        <span>📥 {form.submission_count ?? 0} submissions</span>
        <span>📅 {fmt(form.updated_at)}</span>
      </div>

      {form.status === 'published' && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input readOnly value={publicUrl} style={{ flex: 1, background: 'var(--xfd-input)', border: '1px solid var(--xfd-border)', borderRadius: 6, padding: '5px 10px', color: 'var(--xfd-muted)', fontSize: 11, fontFamily: 'monospace' }} />
          <button onClick={copyUrl} title="Copy URL" style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'none', color: copied ? '#22c55e' : 'var(--xfd-muted)', cursor: 'pointer', fontSize: 12 }}>{copied ? '✓' : '⎘'}</button>
          <button onClick={() => window.open(publicUrl, '_blank')} title="Open form" style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 12 }}>↗</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
        <button onClick={() => onDesign(form.id)} style={{ flex: 1, minWidth: 80, padding: '7px 14px', borderRadius: 7, border: 'none', background: 'var(--accent-color, #6366f1)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Design</button>
        <button onClick={() => onViewSubmissions(form)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>Submissions</button>
        <button onClick={() => onViewAnalytics(form)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: 13 }}>📊 Analytics</button>
        <button onClick={() => onToggleStatus(form)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'none', color: form.status === 'published' ? '#f59e0b' : '#22c55e', cursor: 'pointer', fontSize: 13 }}>{form.status === 'published' ? 'Unpublish' : 'Publish'}</button>
        <button onClick={() => onDuplicate(form)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>Duplicate</button>
        {onMove && <button onClick={() => onMove(form)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>Move</button>}
        <button onClick={() => onDelete(form.id)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.3)', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>Delete</button>
      </div>
    </div>
  );
}

export default function XavvyForms({ authToken, workspaceId, onDesign, onBack, isFormsOnly }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [submissionsFor, setSubmissionsFor] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null);
  const [movingForm, setMovingForm] = useState(null);
  const [moveWorkspaces, setMoveWorkspaces] = useState([]);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const url = workspaceId ? `/xavvy-forms?workspace_id=${workspaceId}` : '/xavvy-forms';
    API(url).then(r => r.json()).then(d => setForms(Array.isArray(d) ? d : [])).finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(false);
    const defaultDef = {
      settings: { title: newName.trim(), description: '', submitLabel: 'Submit', successMessage: 'Thank you for your submission!', redirectUrl: '', honeypot: true, multiStep: false, showProgressBar: false, autoSave: true, prefillFromUrl: true, customCss: '' },
      theme: { primaryColor: '#6366f1', backgroundColor: 'var(--xfd-input)', cardBackground: 'var(--xfd-panel)', textColor: 'var(--xfd-text)', borderRadius: '10px', fontFamily: 'Inter, sans-serif', layout: 'card' },
      elements: [],
    };
    const payload = { name: newName.trim(), definition: defaultDef };
    if (workspaceId) payload.workspace_id = workspaceId;
    const res = await API('/xavvy-forms', { method: 'POST', body: JSON.stringify(payload) });
    const form = await res.json();
    setNewName('');
    onDesign(form.id);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this form and all its submissions? This cannot be undone.')) return;
    await API(`/xavvy-forms/${id}`, { method: 'DELETE' });
    setForms(f => f.filter(x => x.id !== id));
  };

  const handleToggleStatus = async (form) => {
    const next = form.status === 'published' ? 'draft' : 'published';
    const res = await API(`/xavvy-forms/${form.id}`, { method: 'PUT', body: JSON.stringify({ status: next }) });
    const updated = await res.json();
    setForms(f => f.map(x => x.id === form.id ? { ...x, status: updated.status } : x));
  };

  const handleDuplicate = async (form) => {
    const fullRes = await API(`/xavvy-forms/${form.id}`);
    const full = await fullRes.json();
    const dupPayload = { name: `${full.name} (copy)`, definition: full.definition };
    if (workspaceId) dupPayload.workspace_id = workspaceId;
    const res = await API('/xavvy-forms', { method: 'POST', body: JSON.stringify(dupPayload) });
    const newForm = await res.json();
    load();
  };

  const handleOpenMove = async (form) => {
    setMovingForm(form);
    setMoveTargetId('');
    try {
      const res = await API('/workspaces/?member_only=true');
      const list = await res.json();
      setMoveWorkspaces((list || []).filter(ws => ws.id !== workspaceId));
    } catch { setMoveWorkspaces([]); }
  };

  const handleMove = async () => {
    if (!movingForm || !moveTargetId) return;
    setMoveLoading(true);
    try {
      await API(`/xavvy-forms/${movingForm.id}/move`, { method: 'POST', body: JSON.stringify({ target_workspace_id: moveTargetId }) });
      setMovingForm(null);
      load();
    } catch (e) { alert('Failed to move form'); }
    finally { setMoveLoading(false); }
  };

  const filtered = forms.filter(f => {
    if (filter === 'published' && f.status !== 'published') return false;
    if (filter === 'draft' && f.status !== 'draft') return false;
    if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--xfd-input)', padding: '32px 28px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {submissionsFor && <SubmissionsPanel form={submissionsFor} authToken={authToken} onClose={() => setSubmissionsFor(null)} />}
      {analyticsFor && <AnalyticsPanel form={analyticsFor} onClose={() => setAnalyticsFor(null)} />}

      {/* Move form modal */}
      {movingForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setMovingForm(null)}>
          <div style={{ background: 'var(--xfd-panel)', border: '1px solid var(--xfd-border)', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, color: 'var(--xfd-text)' }}>Move "{movingForm.name}"</h3>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 13, color: 'var(--xfd-muted)' }}>Select target workspace</label>
            <select
              value={moveTargetId}
              onChange={e => setMoveTargetId(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 14, marginBottom: 20 }}
            >
              <option value="">Select workspace…</option>
              {moveWorkspaces.map(ws => (
                <option key={ws.id} value={ws.id}>{ws.name}{ws.is_default ? ' (default)' : ''}</option>
              ))}
            </select>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setMovingForm(null)} style={{ padding: '8px 16px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handleMove} disabled={!moveTargetId || moveLoading} style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: 'var(--accent-color, #6366f1)', color: '#fff', cursor: moveTargetId ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, opacity: moveTargetId ? 1 : 0.5 }}>{moveLoading ? 'Moving…' : 'Move'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, gap: 16, flexWrap: 'wrap' }}>
        <div>
          {onBack && !isFormsOnly && <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--xfd-dim)', cursor: 'pointer', fontSize: 13, padding: '0 0 8px', display: 'flex', alignItems: 'center', gap: 4 }}>← Back</button>}
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--xfd-text)' }}>{isFormsOnly ? 'Forms' : 'Xavvy Forms'}</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--xfd-dim)', fontSize: 13 }}>
            {isFormsOnly
              ? 'Design, publish, and manage your forms.'
              : 'Design, publish, and manage forms. Use them as workflow triggers or mid-flow inputs.'}
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--accent-color, #6366f1)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          + New Form
        </button>
      </div>

      {/* New form dialog */}
      {creating && (
        <div style={{ background: 'var(--xfd-panel)', border: '1px solid var(--xfd-border)', borderRadius: 10, padding: 20, marginBottom: 24, display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            autoFocus
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
            placeholder="Form name…"
            style={{ flex: 1, padding: '9px 14px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 14 }}
          />
          <button onClick={handleCreate} disabled={!newName.trim()} style={{ padding: '9px 18px', borderRadius: 7, border: 'none', background: 'var(--accent-color, #6366f1)', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: newName.trim() ? 1 : 0.5 }}>Create & Design</button>
          <button onClick={() => { setCreating(false); setNewName(''); }} style={{ padding: '9px 14px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer' }}>Cancel</button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search forms…"
          style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'var(--xfd-panel)', color: 'var(--xfd-text)', fontSize: 13, width: 220 }}
        />
        {['all', 'published', 'draft'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: filter === f ? 'rgba(99,102,241,0.15)' : 'none', color: filter === f ? 'var(--accent-color, #6366f1)' : 'var(--xfd-dim)', cursor: 'pointer', fontSize: 13, textTransform: 'capitalize' }}>{f}</button>
        ))}
        <span style={{ marginLeft: 'auto', color: 'var(--xfd-dim)', fontSize: 12 }}>{filtered.length} form{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--xfd-dim)' }}>Loading forms…</div>
      ) : forms.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--xfd-text)', marginBottom: 8 }}>No forms yet</div>
          <div style={{ color: 'var(--xfd-dim)', marginBottom: 24, fontSize: 14 }}>Create your first form to get started</div>
          <button onClick={() => setCreating(true)} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: 'var(--accent-color, #6366f1)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>+ Create Form</button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--xfd-dim)' }}>No forms match your filter.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filtered.map(form => (
            <FormCard
              key={form.id}
              form={form}
              authToken={authToken}
              onDesign={onDesign}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              onDuplicate={handleDuplicate}
              onMove={handleOpenMove}
              onViewSubmissions={setSubmissionsFor}
              onViewAnalytics={setAnalyticsFor}
            />
          ))}
        </div>
      )}

      {/* Upsell banner for forms-only mode */}
      {isFormsOnly && (
        <div style={{
          marginTop: 32, padding: '16px 24px', borderRadius: 10,
          background: 'linear-gradient(90deg, rgba(99,102,241,0.08), rgba(168,85,247,0.08))',
          border: '1px solid rgba(99,102,241,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--xfd-text)', marginBottom: 2 }}>Need workflow automation?</div>
            <div style={{ fontSize: 12, color: 'var(--xfd-muted)' }}>Upgrade to xavvy Flow to trigger Slack messages, create CRM records, and run AI agents from form submissions.</div>
          </div>
          <a href="https://xavvy.ai" target="_blank" rel="noopener noreferrer" style={{
            padding: '8px 18px', borderRadius: 7, border: '1px solid var(--accent-color, #6366f1)',
            background: 'transparent', color: 'var(--accent-color, #6366f1)',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap',
          }}>Learn about Flow</a>
        </div>
      )}
    </div>
  );
}
